// Shared Lead Data Store - Keeps leads synchronized across components

interface Lead {
  id: string;
  name: string;
  company: string;
  email: string;
  phone: string;
  status: 'New' | 'Contacted' | 'Qualified' | 'Lost';
  source: string;
  value: number;
  createdAt: string;
  avatar?: string;
  location?: string;
}

class LeadsStore {
  private leads: Lead[] = [];
  private listeners: Set<(leads: Lead[]) => void> = new Set();

  // Set the current leads data
  setLeads(leads: Lead[]): void {
    this.leads = [...leads];
    this.notifyListeners();
  }

  // Get current leads data
  getLeads(): Lead[] {
    return [...this.leads];
  }

  // Add a lead
  addLead(lead: Lead): void {
    this.leads = [...this.leads, lead];
    this.notifyListeners();
  }

  // Update a lead
  updateLead(id: string, updates: Partial<Lead>): void {
    this.leads = this.leads.map(lead =>
      lead.id === id ? { ...lead, ...updates } : lead
    );
    this.notifyListeners();
  }

  // Delete a lead
  deleteLead(id: string): void {
    this.leads = this.leads.filter(lead => lead.id !== id);
    this.notifyListeners();
  }

  // Subscribe to changes
  subscribe(listener: (leads: Lead[]) => void): () => void {
    this.listeners.add(listener);
    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  // Notify all listeners of changes
  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener(this.getLeads());
      } catch (error) {
        console.error('Error in leads store listener:', error);
      }
    });
  }

  // Check if we have leads loaded
  hasLeads(): boolean {
    return this.leads.length > 0;
  }

  // Debug method to see current leads
  debugCurrentLeads(): void {
    console.log('Current leads in store:', this.leads.map(l => `${l.name} - ${l.company} - ${l.email}`));
  }

  // Get leads by status
  getLeadsByStatus(status: Lead['status']): Lead[] {
    return this.leads.filter(lead => lead.status === status);
  }

  // Get high-value leads
  getHighValueLeads(minValue: number = 50000): Lead[] {
    return this.leads
      .filter(lead => lead.value >= minValue)
      .sort((a, b) => b.value - a.value);
  }
}

// Export singleton instance
export const leadsStore = new LeadsStore();
export type { Lead };